import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Building2, Scale, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1A1A2E] text-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
            <h1 className="text-3xl font-bold">Important Disclaimers</h1>
          </div>
          <p className="text-white/70">Last updated: December 2024</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Primary Disclaimer Box */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-amber-800 mb-2">Important Notice</h2>
              <p className="text-amber-900 font-medium text-lg leading-relaxed">
                This website is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. All real estate purchases involve risk. Buyers should consult with their tax, legal, and financial advisors prior to making any purchase decisions.
              </p>
            </div>
          </div>
        </div>

        {/* Key Disclaimers */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Key Disclosures</h2>
          
          <div className="space-y-6">
            <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-semibold text-gray-900 mb-2">Brokerage Services</h3>
              <p className="text-gray-700">
                Krieger Real Estate LLC is a licensed real estate broker. All real estate brokerage services are provided in accordance with applicable state laws and regulations.
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-amber-500">
              <h3 className="font-semibold text-gray-900 mb-2">1031 Exchange Notice</h3>
              <p className="text-gray-700">
                1031 exchanges are subject to strict IRS rules and timelines, including identification and closing deadlines. We do not provide tax or legal advice. Please consult your qualified intermediary and tax advisor.
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-gray-900 mb-2">Investment Structure</h3>
              <p className="text-gray-700">
                We do not offer fractionalized or pooled investment vehicles. All properties are offered as direct ownership real estate interests unless otherwise stated.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-12 prose prose-slate max-w-none">
          
          <h2 className="flex items-center gap-2">
            <Scale className="w-6 h-6 text-gray-600" />
            General Disclaimer
          </h2>
          
          <h3 className="mt-6 mb-3">No Tax or Legal Advice</h3>
          <p className="mb-4">
            <strong>1031Condo.com does not provide tax, legal, or financial advice.</strong> The information on this website, including but not limited to property listings, calculators, guides, and educational materials, is provided for general informational and demonstration purposes only.
          </p>
          <ul className="mb-6 space-y-2">
            <li>Tax calculations and savings estimates are approximations only</li>
            <li>All property values, rental income projections, and expense estimates are estimates and may vary significantly</li>
            <li>Cap rates and financial metrics are calculated using estimated figures and do not represent projections of future performance</li>
            <li>Past performance is not indicative of future results</li>
          </ul>

          <h3 className="mt-8 mb-3">Consult Qualified Professionals</h3>
          <p className="mb-4">
            Before entering into any 1031 exchange transaction, you should consult with:
          </p>
          <ul className="mb-6 space-y-2">
            <li><strong>A Qualified Intermediary (QI):</strong> An independent party who facilitates the 1031 exchange and holds the funds between transactions</li>
            <li><strong>A Tax Professional:</strong> A CPA or tax attorney who can advise on the tax implications specific to your situation</li>
            <li><strong>A Real Estate Attorney:</strong> Legal counsel familiar with real estate transactions and 1031 exchanges</li>
            <li><strong>A Licensed Real Estate Professional:</strong> A broker or agent licensed in the relevant state(s)</li>
          </ul>

          <h3 className="mt-8 mb-3">Investment Risks</h3>
          <p className="mb-4">
            Real estate investments involve significant risks, including but not limited to:
          </p>
          <ul className="mb-6 space-y-2">
            <li>Market fluctuations and potential loss of principal</li>
            <li>Illiquidity of real estate assets</li>
            <li>Vacancy and tenant-related risks</li>
            <li>Changes in tax laws and regulations</li>
            <li>Property-specific issues (maintenance, HOA changes, assessments)</li>
            <li>Interest rate changes affecting financing</li>
          </ul>

          <h3 className="mt-8 mb-3">1031 Exchange Requirements</h3>
          <p className="mb-4">
            IRS Section 1031 exchanges have strict requirements including, but not limited to:
          </p>
          <ul className="mb-4 space-y-2">
            <li>45-day identification period for replacement properties</li>
            <li>180-day exchange period to complete the transaction</li>
            <li>Like-kind property requirements</li>
            <li>Use of a qualified intermediary</li>
            <li>Reinvestment of all proceeds to fully defer taxes</li>
          </ul>
          <p className="mb-6">
            Failure to comply with these requirements may result in a taxable event. <strong>Consult a qualified 1031 exchange intermediary for specific guidance.</strong>
          </p>

          <hr className="my-8" />

          <h2 className="flex items-center gap-2 mt-8">
            <MapPin className="w-6 h-6 text-gray-600" />
            State Real Estate Disclosures
          </h2>

          <h3 className="flex items-center gap-2 mt-6 mb-4">
            <Building2 className="w-5 h-5" />
            New York State Disclosures
          </h3>
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6">
            <p className="font-medium text-gray-800 mb-3">New York Real Property Law Disclosure</p>
            <p className="mb-4">
              In accordance with New York Real Property Law Section 443, the following disclosure is provided:
            </p>
            <p className="mb-4">
              1031Condo.com is owned and operated by <strong>Krieger Real Estate LLC</strong>, a licensed real estate broker in New York State — <strong>License #10491202190</strong>.
            </p>
            <ul className="mb-4 space-y-2">
              <li>Property listings are provided for informational purposes only</li>
              <li>All real estate transactions must be conducted through licensed New York real estate brokers</li>
              <li>New York requires disclosure of agency relationships in real estate transactions</li>
            </ul>
            <p className="mt-5 pt-4 border-t border-gray-200">
              <strong>Fair Housing Notice:</strong> This website complies with the New York State Human Rights Law and the Fair Housing Act. We do not discriminate based on race, color, religion, sex, national origin, familial status, disability, sexual orientation, gender identity, military status, age, marital status, or any other protected class.
            </p>
            <p className="mt-4">
              <strong>New York Standard Operating Procedure:</strong> Buyers and sellers in New York are entitled to receive a disclosure form describing the role of brokers and agents in a transaction. Ask for this form if not provided.
            </p>
          </div>

          <h3 className="flex items-center gap-2 mt-8 mb-4">
            <Building2 className="w-5 h-5" />
            Florida State Disclosures
          </h3>
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
            <p className="font-medium text-gray-800 mb-3">Florida Real Estate Disclosure</p>
            <p className="mb-4">
              In accordance with Florida Statutes Chapter 475, the following disclosure is provided:
            </p>
            <p className="mb-4">
              1031Condo.com is owned and operated by <strong>Krieger Real Estate LLC</strong>, a licensed real estate broker in Florida — <strong>License #CQ1072150</strong>.
            </p>
            <ul className="mb-4 space-y-2">
              <li>All real estate sales must be conducted by or through licensed Florida real estate brokers</li>
              <li>Florida law requires written disclosure of agency relationships</li>
            </ul>
            <p className="mt-5 pt-4 border-t border-gray-200">
              <strong>Brokerage Relationship Disclosure:</strong> Florida law requires real estate licensees to disclose their role in a transaction. Authorized brokerage relationships include transaction broker, single agent, and no brokerage relationship. Request the appropriate disclosure form before engaging in any transaction.
            </p>
            <p className="mt-4">
              <strong>Fair Housing Notice:</strong> This website complies with the Florida Fair Housing Act (Florida Statutes Section 760.20-760.37) and the federal Fair Housing Act. We do not discriminate based on race, color, religion, sex, national origin, handicap, familial status, or any other protected class.
            </p>
            <p className="mt-4">
              <strong>Condominium Disclosure:</strong> Per Florida Statute 718.503, purchasers of condominium units have the right to void the contract within specific timeframes after receiving required documents. Purchasers should carefully review all condominium documents including the declaration, bylaws, rules, financial statements, and FAQ sheet.
            </p>
            <p className="mt-4">
              <strong>Property Condition:</strong> Florida Statute 475.278 requires disclosure of known material defects. Buyers are encouraged to conduct independent inspections and due diligence.
            </p>
          </div>

          <hr className="my-10" />

          <h2 className="mb-3">Calculator and Tool Disclaimer</h2>
          <p className="mb-4">
            The calculators and estimation tools provided on this website, including but not limited to the Tax Savings Calculator and Cap Rate Calculator, are:
          </p>
          <ul className="mb-4 space-y-2">
            <li>For demonstration and educational purposes only</li>
            <li>Based on simplified assumptions that may not reflect your actual situation</li>
            <li>Not a substitute for professional tax or financial advice</li>
            <li>Subject to errors and should not be relied upon for actual transactions</li>
          </ul>
          <p className="mb-6">
            <strong>Actual tax savings and financial outcomes may differ materially from any estimates provided by these tools.</strong>
          </p>

          <h2 className="mt-8 mb-3">Property Information Disclaimer</h2>
          <p className="mb-4">
            Property information displayed on this website:
          </p>
          <ul className="mb-4 space-y-2">
            <li>Is obtained from various sources believed to be reliable but is not guaranteed</li>
            <li>May not be current or accurate at the time of viewing</li>
            <li>Should be independently verified before any purchase decision</li>
            <li>May change without notice</li>
          </ul>
          <p className="mb-6">
            1031Condo.com makes no representations or warranties regarding the accuracy, completeness, or timeliness of any property information.
          </p>

          <h2 className="mt-8 mb-3">No Guarantees</h2>
          <p className="mb-4">
            1031Condo.com does not guarantee:
          </p>
          <ul className="mb-6 space-y-2">
            <li>The availability of any listed property</li>
            <li>The accuracy of any financial projections or estimates</li>
            <li>The successful completion of any 1031 exchange</li>
            <li>Any specific financial outcomes or tax outcomes</li>
            <li>The qualifications or performance of any third-party professionals</li>
          </ul>

          <h2 className="mt-8 mb-3">Contact Information</h2>
          <p className="mb-4">For questions about these disclaimers, contact us:</p>
          <ul className="mb-4 space-y-2">
            <li>Contact: <a href="/contact">Send us a message</a></li>
            <li>Company: Krieger Real Estate LLC</li>
            <li>Website: 1031Condo.com</li>
          </ul>

          <div className="mt-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700 mb-3">
              <strong>Company Information:</strong> 1031Condo.com is owned and operated by Krieger Real Estate LLC, a licensed real estate broker in:
            </p>
            <ul className="text-sm text-gray-700 list-disc list-inside space-y-1 mb-0">
              <li><strong>New York State</strong> - License #10491202190</li>
              <li><strong>Florida</strong> - License #CQ1072150</li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex gap-4">
          <Link to="/privacy">
            <Button variant="outline">Privacy Policy</Button>
          </Link>
          <Link to="/terms">
            <Button variant="outline">Terms of Service</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
